
import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Settings, Home, Zap, Trophy, Cog, Target, DollarSign, CreditCard, ShoppingBag } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Navigation from "./Navigation";
import HomeNavigation from "./HomeNavigation";
import MobileNavigation from "./MobileNavigation";
import { toast } from "@/hooks/use-toast";
import { isAndroidTWA } from "@/utils/runtimeContext";

const Header = () => {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inTWA = isAndroidTWA();

  const handleLogout = async () => {
    try {
      console.log("Starting logout process...");
      await logout();
      
      // Clear any stored data
      localStorage.clear();
      sessionStorage.clear();
      
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
      
      // Force navigation to home page
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      
      // Force logout even if there's an error
      localStorage.clear();
      sessionStorage.clear();
      
      toast({
        title: "Desconectado",
        description: "Você foi desconectado.",
        variant: "destructive",
      });
      
      // Force navigation to home page
      window.location.href = "/";
    }
  };

  const handleNavigation = (path: string) => {
    try {
      console.log(`Navigating to: ${path}`);
      navigate(path);
    } catch (error) {
      console.error("Navigation error:", error);
      // Fallback to window.location
      window.location.href = path;
    }
  };

  const getUserDisplayName = () => {
    if (!user) return "";
    
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split("@")[0];
    
    return "Usuário";
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    return displayName.substring(0, 2).toUpperCase();
  };

  if (!user) {
    return (
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 relative z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo sempre visível */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            onClick={(e) => {
              e.preventDefault();
              handleNavigation("/");
            }}
          >
            <h1 className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
              Palpite Guru
            </h1>
          </Link>
          
          {/* Desktop Navigation - hidden on mobile */}
          <div className="hidden md:block">
            {location.pathname === "/" ? <HomeNavigation /> : <Navigation />}
          </div>
          
          {/* Desktop buttons - hidden on mobile */}
          <div className="hidden md:flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => handleNavigation("/login")}
              className="text-foreground hover:text-foreground hover:bg-muted/50 active:bg-muted transition-colors touch-manipulation min-h-[44px] px-4"
            >
              Entrar
            </Button>
            <Button 
              onClick={() => handleNavigation("/cadastro")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 transition-colors touch-manipulation min-h-[44px] px-6"
            >
              Cadastrar
            </Button>
          </div>
          
          {/* Mobile Navigation with Register Button (Entrar segue na barra inferior e no menu) */}
          <div className="md:hidden flex items-center gap-2">
            <Button 
              size="sm"
              onClick={() => handleNavigation("/cadastro")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 transition-colors touch-manipulation min-h-[40px] px-4 text-sm font-semibold"
            >
              Cadastrar
            </Button>
            <MobileNavigation />
          </div>

        </div>
      </header>
    );
  }

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 relative z-50">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo sempre visível */}
        <Link 
          to="/" 
          className="flex items-center gap-2"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation("/");
          }}
        >
          <h1 className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
            Palpite Guru
          </h1>
        </Link>
        
        {/* Desktop Navigation - hidden on mobile */}
        <div className="hidden md:block">
          {location.pathname === "/" ? <HomeNavigation /> : <Navigation />}
        </div>
        
        {/* Desktop user menu - hidden on mobile */}
        <div className="hidden md:flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">
            Olá, <span className="font-medium text-foreground">{getUserDisplayName()}</span>
          </span>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full" disabled={isLoading}>
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" />
                  <AvatarFallback>{getUserInitials()}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  <p className="font-medium">{getUserDisplayName()}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <DropdownMenuSeparator />
              
              {/* Ferramentas de navegação */}
              <DropdownMenuItem 
                onClick={() => handleNavigation("/dashboard")}
                disabled={isLoading}
              >
                <Home className="mr-2 h-4 w-4" />
                Dashboard do Usuário
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/gerar-jogos")}
                disabled={isLoading}
              >
                <Zap className="mr-2 h-4 w-4" />
                Gerar Jogos IA
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/puxadas")}
                disabled={isLoading}
              >
                <Target className="mr-2 h-4 w-4" />
                Puxadas do Bicho
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/loterias")}
                disabled={isLoading}
              >
                <Trophy className="mr-2 h-4 w-4" />
                Loterias Caixa
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/software")}
                disabled={isLoading}
              >
                <Cog className="mr-2 h-4 w-4" />
                Soft. Turb. IA
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/metodos")}
                disabled={isLoading}
              >
                🎯
                Métodos c/ IA
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/resultados-bicho")}
                disabled={isLoading}
              >
                📊 Resultado RIO
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/loja")}
                disabled={isLoading}
              >
                <ShoppingBag className="mr-2 h-4 w-4" />
                Loja
              </DropdownMenuItem>
              
              {!inTWA && (
                <DropdownMenuItem
                  onClick={() => handleNavigation("/pagamento")}
                  disabled={isLoading}
                >
                  💳 Pagamento PIX
                </DropdownMenuItem>
              )}

              {!inTWA && (
                <DropdownMenuItem
                  onClick={() => handleNavigation("/planos")}
                  disabled={isLoading}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Planos/Pagamentos
                </DropdownMenuItem>
              )}
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/afiliados")}
                disabled={isLoading}
              >
                <DollarSign className="mr-2 h-4 w-4" />
                Afiliados
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem 
                onClick={() => handleNavigation("/minha-conta")}
                disabled={isLoading}
              >
                <User className="mr-2 h-4 w-4" />
                Minha Conta
              </DropdownMenuItem>
              
              {user.role === "admin" && (
                <DropdownMenuItem 
                  onClick={() => handleNavigation("/admin")}
                  disabled={isLoading}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Administração
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem 
                onClick={handleLogout}
                disabled={isLoading}
                className="text-red-600 focus:text-red-600"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {isLoading ? "Saindo..." : "Sair"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Mobile Navigation - indicador de conectado + menu */}
        <div className="md:hidden flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground max-w-[130px]">
            <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
            <span className="truncate">Conectado · {getUserDisplayName()}</span>
          </span>
          <MobileNavigation />
        </div>

      </div>
    </header>
  );
};

export default Header;
